import type { DeckProject } from './types'

export interface ProjectValidationIssue {
  path: string
  message: string
}

export class ProjectValidationError extends Error {
  readonly issues: ProjectValidationIssue[]

  constructor(issues: ProjectValidationIssue[]) {
    const first = issues[0]
    super(first ? `This project is incomplete or damaged: ${first.path} ${first.message}` : 'This project is incomplete or damaged.')
    this.name = 'ProjectValidationError'
    this.issues = issues
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)

const isNonEmptyString = (value: unknown) => typeof value === 'string' && value.trim().length > 0
const isFiniteNumber = (value: unknown) => typeof value === 'number' && Number.isFinite(value)

const gradientIssues = (value: unknown, path: string): ProjectValidationIssue[] => {
  if (!isRecord(value) || !Array.isArray(value.stops)) return [{ path, message: 'must include gradient stops.' }]
  const issues: ProjectValidationIssue[] = []
  if (!['linear', 'radial'].includes(String(value.type))) issues.push({ path: `${path}.type`, message: 'must be linear or radial.' })
  for (const field of ['angle', 'centerX', 'centerY', 'radius'] as const) {
    if (!isFiniteNumber(value[field])) issues.push({ path: `${path}.${field}`, message: 'must be a finite number.' })
  }
  issues.push(...duplicateIdIssues(value.stops, `${path}.stops`))
  value.stops.forEach((stop, index) => {
    if (!isRecord(stop)) return
    if (typeof stop.color !== 'string') issues.push({ path: `${path}.stops[${index}].color`, message: 'must be a string.' })
    if (!isFiniteNumber(stop.position)) issues.push({ path: `${path}.stops[${index}].position`, message: 'must be a finite number.' })
  })
  return issues
}

const duplicateIdIssues = (items: unknown[], path: string) => {
  const issues: ProjectValidationIssue[] = []
  const seen = new Set<string>()
  items.forEach((item, index) => {
    if (!isRecord(item) || !isNonEmptyString(item.id)) {
      issues.push({ path: `${path}[${index}].id`, message: 'must be a non-empty string.' })
      return
    }
    const id = String(item.id)
    if (seen.has(id)) issues.push({ path: `${path}[${index}].id`, message: `duplicates “${id}”.` })
    seen.add(id)
  })
  return issues
}

export const inspectProjectSource = (source: unknown): ProjectValidationIssue[] => {
  if (!isRecord(source)) return [{ path: 'project', message: 'must be an object.' }]
  const issues: ProjectValidationIssue[] = []
  if (!isRecord(source.card)) issues.push({ path: 'card', message: 'must be an object.' })
  for (const field of ['elements', 'rows', 'variables'] as const) {
    const value = source[field]
    if (!Array.isArray(value)) {
      issues.push({ path: field, message: 'must be an array.' })
      continue
    }
    value.forEach((item, index) => {
      if (!isRecord(item)) issues.push({ path: `${field}[${index}]`, message: 'must be an object.' })
    })
  }
  if (source.assets !== undefined && !isRecord(source.assets)) issues.push({ path: 'assets', message: 'must be an object.' })
  if (source.fonts !== undefined && !isRecord(source.fonts)) issues.push({ path: 'fonts', message: 'must be an object.' })
  return issues
}

export const inspectDeckProject = (project: DeckProject): ProjectValidationIssue[] => {
  const source = project as unknown as Record<string, unknown>
  const issues = inspectProjectSource(source)
  if (issues.length) return issues

  for (const field of ['id', 'name', 'createdAt', 'updatedAt'] as const) {
    if (!isNonEmptyString(source[field])) issues.push({ path: field, message: 'must be a non-empty string.' })
  }

  const card = source.card as Record<string, unknown>
  for (const field of ['widthMm', 'heightMm'] as const) {
    if (!isFiniteNumber(card[field]) || Number(card[field]) <= 0) issues.push({ path: `card.${field}`, message: 'must be a positive finite number.' })
  }
  for (const field of ['bleedMm', 'safeMm', 'cornerRadiusMm', 'borderWidthMm', 'borderInnerRadiusMm'] as const) {
    if (!isFiniteNumber(card[field]) || Number(card[field]) < 0) issues.push({ path: `card.${field}`, message: 'must be a non-negative finite number.' })
  }
  issues.push(...gradientIssues(card.backgroundGradient, 'card.backgroundGradient'))

  const variables = source.variables as unknown[]
  const rows = source.rows as unknown[]
  const elements = source.elements as unknown[]
  issues.push(...duplicateIdIssues(variables, 'variables'))
  issues.push(...duplicateIdIssues(rows, 'rows'))
  issues.push(...duplicateIdIssues(elements, 'elements'))

  const variableTypes = new Set(['text', 'number', 'color', 'image', 'boolean'])
  variables.forEach((item, index) => {
    if (!isRecord(item)) return
    if (!isNonEmptyString(item.name)) issues.push({ path: `variables[${index}].name`, message: 'must be a non-empty string.' })
    if (!variableTypes.has(String(item.type))) issues.push({ path: `variables[${index}].type`, message: `contains unsupported value “${String(item.type)}”.` })
    if (!isFiniteNumber(item.columnWidthPx) || Number(item.columnWidthPx) <= 0) issues.push({ path: `variables[${index}].columnWidthPx`, message: 'must be a positive finite number.' })
  })

  rows.forEach((item, index) => {
    if (!isRecord(item)) return
    if (typeof item.name !== 'string') issues.push({ path: `rows[${index}].name`, message: 'must be a string.' })
    if (!isRecord(item.values)) issues.push({ path: `rows[${index}].values`, message: 'must be an object.' })
    else Object.entries(item.values).forEach(([key, value]) => {
      if (typeof value !== 'string' && !isFiniteNumber(value)) issues.push({ path: `rows[${index}].values.${key}`, message: 'must be text or a finite number.' })
    })
  })

  const elementTypes = new Set(['text', 'rectangle', 'ellipse', 'polygon', 'star', 'arc', 'ring', 'line'])
  elements.forEach((item, index) => {
    if (!isRecord(item)) return
    if (!elementTypes.has(String(item.type))) issues.push({ path: `elements[${index}].type`, message: `contains unsupported value “${String(item.type)}”.` })
    if (!isNonEmptyString(item.name)) issues.push({ path: `elements[${index}].name`, message: 'must be a non-empty string.' })
    for (const field of ['x', 'y', 'width', 'height', 'rotation', 'opacity', 'strokeWidth', 'cornerRadius', 'polygonSides', 'starPoints', 'starInnerRatio', 'arcStartAngle', 'arcSweepAngle', 'ringInnerRatio', 'fontSize', 'letterSpacing', 'lineHeight', 'imageScale', 'imageOffsetX', 'imageOffsetY', 'imageRotation', 'imageOpacity'] as const) {
      if (!isFiniteNumber(item[field])) issues.push({ path: `elements[${index}].${field}`, message: 'must be a finite number.' })
    }
    if (!isRecord(item.bindings)) issues.push({ path: `elements[${index}].bindings`, message: 'must be an object.' })
    issues.push(...gradientIssues(item.gradient, `elements[${index}].gradient`))
    if (!isRecord(item.shadow)) issues.push({ path: `elements[${index}].shadow`, message: 'must be an object.' })
    else for (const field of ['opacity', 'offsetX', 'offsetY', 'blur'] as const) {
      if (!isFiniteNumber(item.shadow[field])) issues.push({ path: `elements[${index}].shadow.${field}`, message: 'must be a finite number.' })
    }
  })

  if (!isRecord(source.assets)) issues.push({ path: 'assets', message: 'must be an object.' })
  else Object.entries(source.assets).forEach(([key, asset]) => {
    if (!isRecord(asset)) {
      issues.push({ path: `assets.${key}`, message: 'must be an object.' })
      return
    }
    if (!isNonEmptyString(asset.id) || asset.id !== key) issues.push({ path: `assets.${key}.id`, message: 'must match its asset key.' })
    if (!isNonEmptyString(asset.dataUrl) || !String(asset.dataUrl).startsWith('data:')) issues.push({ path: `assets.${key}.dataUrl`, message: 'must contain embedded file data.' })
  })

  if (!isRecord(source.fonts)) issues.push({ path: 'fonts', message: 'must be an object.' })
  else Object.entries(source.fonts).forEach(([key, family]) => {
    if (!isRecord(family)) {
      issues.push({ path: `fonts.${key}`, message: 'must be an object.' })
      return
    }
    if (!isNonEmptyString(family.id) || family.id !== key) issues.push({ path: `fonts.${key}.id`, message: 'must match its font key.' })
    if (!Array.isArray(family.faces)) issues.push({ path: `fonts.${key}.faces`, message: 'must be an array.' })
  })

  if (!isRecord(source.render)) issues.push({ path: 'render', message: 'must be an object.' })
  else {
    if (![150, 300, 600].includes(Number(source.render.dpi))) issues.push({ path: 'render.dpi', message: 'must be 150, 300, or 600.' })
    if (!['boardgamesmaker', 'home-print', 'custom'].includes(String(source.render.profile))) issues.push({ path: 'render.profile', message: 'contains an unsupported output profile.' })
    if (!['a4', 'letter', 'custom'].includes(String(source.render.pageSize))) issues.push({ path: 'render.pageSize', message: 'contains an unsupported paper size.' })
    if (!['portrait', 'landscape'].includes(String(source.render.orientation))) issues.push({ path: 'render.orientation', message: 'must be portrait or landscape.' })
    for (const field of ['pageWidthMm', 'pageHeightMm'] as const) {
      if (!isFiniteNumber(source.render[field]) || Number(source.render[field]) <= 0) issues.push({ path: `render.${field}`, message: 'must be a positive finite number.' })
    }
    for (const field of ['marginMm', 'gapMm'] as const) {
      if (!isFiniteNumber(source.render[field]) || Number(source.render[field]) < 0) issues.push({ path: `render.${field}`, message: 'must be a non-negative finite number.' })
    }
  }
  if (!isRecord(source.dataSettings)) issues.push({ path: 'dataSettings', message: 'must be an object.' })
  else if (!isFiniteNumber(source.dataSettings.cardNameWidthPx) || Number(source.dataSettings.cardNameWidthPx) <= 0) issues.push({ path: 'dataSettings.cardNameWidthPx', message: 'must be a positive finite number.' })
  return issues.slice(0, 50)
}

export const assertProjectSource = (source: unknown) => {
  const issues = inspectProjectSource(source)
  if (issues.length) throw new ProjectValidationError(issues)
}

export const assertDeckProject = (project: DeckProject) => {
  const issues = inspectDeckProject(project)
  if (issues.length) throw new ProjectValidationError(issues)
}
